import React, {Component, Context, createContext, JSX} from 'react';
import {Agent, WorkflowNode} from "../../../types/workflow";
import {Operator, Tool} from "../../../types/entities";
import {apiUrl, hostUrl, UseUrlManagerCleanUrl} from "../../utils/url-manager";
import {read_cookie} from "sfcookies";
import {Edge} from "@xyflow/react";
import {
    fetchOperatorConnection,
    operatorSocketUrl,
    operatorSocketProtocols,
    OperatorConnection
} from "../../../providers/operatorConnection";

export const WorkflowContext: Context<any> = createContext(null);

type WorkflowStateType = {
    mode: string,
    currentRecord: any,
    nodes: WorkflowNode[],
    edges: any[],
    settings: any,
    executionSettings: any,
    showModal: boolean,
    selectedNode: WorkflowNode,
    tools: Tool[],
    toolConfigureModalOpen: boolean,
    editingTool: Tool,
    showIfModal: boolean,
    editableIfNodeConfiguration: any,
    operator: any,
    testSocket: any,
    activeTestMessages: any,
    connectorId: string,
    testOperatorThinking: boolean,
    adminUsername: string,
    showScraperModal: boolean,
    activeEditScraperNode: Node,
    showSitemapModal: boolean
    activeEditSitemapNode: Node,
    showHQFetchModal: boolean,
    activeEditHQFetchNode: Node,
    showHttpModal: boolean,
    activeEditHttpNode: Node,
    showMergeModal: boolean,
    activeEditMergeNode: Node,
    showParallelModal: boolean,
    activeEditParallelNode: Node,
    showLoopModal: boolean,
    activeEditLoopNode: Node,
    showSwitchModal: boolean,
    activeEditSwitchNode: Node,
    showHQUploadModal: boolean,
    activeEditHQUploadNode: Node,
    showHQCreateModal: boolean,
    activeEditHQCreateNode: Node,
    showSummarizeModal: boolean,
    activeEditSummarizeNode: Node,
    showConvertModal: boolean,
    activeEditConvertNode: Node,
    showFilterModal: boolean,
    activeEditFilterNode: Node,
    showExtractionModal: boolean,
    activeExtractionNode: Node,
    showInjectionModal: boolean,
    activeEditInjectionNode: Node,
    showDataResetModal: boolean,
    activeEditDataResetNode: Node,
    showSortModal: boolean,
    activeEditSortNode: Node,
    showSampleDataModal: boolean,
    activeSampleDataNode: Node,
    testComplete: string[]
    testProcessing: string[],
    testErrored: string[],
    errorMessages: any,
    showTestWindow: boolean,
    showHumanModal: boolean,
    activeEditHumanNode: Node,
    interruptKey: string,
    showMoveModal: boolean,
    activeEdiMoveNode: Node,
    workflowExecutionSettings: any,
    showWorkflowExecutionSettingsModal: boolean,
    showErrorMessagesPanel: boolean,
    nodeTestOutputs: any,
    showTestOutputPanel: boolean,
    showControlsDrawer: boolean,
    interruptWaiting: boolean,
    conditionalInterrupt: boolean,
    showUiUpdateModal: boolean,
    activeEditUiUpdateNode: Node,
    activeStatus: { data_type: string, icon: string, value: string }|undefined
}

class WorkflowContextProvider extends Component<any, WorkflowStateType> {
    private buffer: string = "";
    private flushBufferTimer: number|null = null
    private streamingMsgId: string | null = null;

    componentDidMount(): void {
        this.preloadDate();
    }

    componentWillUnmount(): void {
        if (this.state.testSocket !== undefined) {
            this.state.testSocket.send('/stop');
            this.state.testSocket.close();
        }
    }

    constructor(props: any) {
        super(props);
        this.state = {
            mode: this.props.mode,
            currentRecord: this.props.record ?? undefined,
            nodes: [
                {
                    id: 'start_node',
                    data: {
                        label: 'Start',
                        id: '',
                        order: 0,
                        type: '',
                        agent: undefined
                    },
                    position: {
                        x: 200,
                        y: 200,
                    },
                    type: 'workflowStartNode',
                    draggable: false
                },
            ],
            edges: [],
            settings: {
                'memory': false,
                'error_exit': true,
                'default_exit' : 'default'
            },
            executionSettings: {
                cron_expression: ''
            },
            showModal: false,
            selectedNode: undefined,
            tools: [],
            toolConfigureModalOpen: false,
            editingTool: undefined,
            showIfModal: false,
            editableIfNodeConfiguration: undefined,
            operator: undefined,
            testSocket: undefined,
            activeTestMessages: [],
            connectorId: undefined,
            testOperatorThinking: false,
            adminUsername: this.props.adminUsername,
            showScraperModal: false,
            activeEditScraperNode: undefined,
            showSitemapModal: false,
            activeEditSitemapNode: undefined,
            showHQFetchModal: false,
            activeEditHQFetchNode: undefined,
            showHttpModal: false,
            activeEditHttpNode: undefined,
            showMergeModal: false,
            activeEditMergeNode: undefined,
            showParallelModal: false,
            activeEditParallelNode: undefined,
            showLoopModal: false,
            activeEditLoopNode: undefined,
            showSwitchModal: false,
            activeEditSwitchNode: undefined,
            showHQUploadModal: false,
            activeEditHQUploadNode: undefined,
            showHQCreateModal: false,
            activeEditHQCreateNode: undefined,
            showSummarizeModal: false,
            activeEditSummarizeNode: undefined,
            showConvertModal: false,
            activeEditConvertNode: undefined,
            showFilterModal: false,
            activeEditFilterNode: undefined,
            showExtractionModal: false,
            activeExtractionNode: undefined,
            showInjectionModal: false,
            activeEditInjectionNode: undefined,
            showDataResetModal: false,
            activeEditDataResetNode: undefined,
            showSortModal: false,
            activeEditSortNode: undefined,
            showSampleDataModal: false,
            activeSampleDataNode: undefined,
            testComplete: [],
            testProcessing: [],
            testErrored: [],
            errorMessages: [],
            showTestWindow: false,
            showHumanModal: false,
            activeEditHumanNode: undefined,
            interruptKey: '',
            showMoveModal: false,
            activeEdiMoveNode: undefined,
            workflowExecutionSettings: undefined,
            showWorkflowExecutionSettingsModal: false,
            showErrorMessagesPanel: false,
            nodeTestOutputs: [],
            showTestOutputPanel: false,
            showControlsDrawer: false,
            interruptWaiting: false,
            conditionalInterrupt: false,
            showUiUpdateModal: false,
            activeEditUiUpdateNode: undefined,
            activeStatus: undefined
        };
    }

    private bufferEmailDelta(delta: string): void {
        this.buffer += delta

        if (this.flushBufferTimer) return;

        this.flushBufferTimer = window.setTimeout((): void => {
            this.flushBufferTimer = null;
            this.flushDraftBuffer();
        }, 5)
    }

    private flushDraftBuffer(): void {
        if (!this.streamingMsgId) return;
        if (!this.buffer) return;
        const tokenChunk: string = this.buffer;
        this.buffer = ""

        this.setState( (prev: Readonly<WorkflowStateType>): Readonly<WorkflowStateType> => {
            const idx: number = prev.activeTestMessages.findLastIndex( (m: any): boolean => m.id === this.streamingMsgId );
            let next: any[] = [...prev.activeTestMessages];
            const isLast: boolean = idx === next.length - 1;
            if (idx === -1 || !isLast) {
                 next = [...next, { "id": this.streamingMsgId, message: tokenChunk, 'type': 'token' }];
            } else {
                const m: any = next[idx];
                next[idx] = {
                    ...m,
                    message: m.message + tokenChunk
                };
            }
            return { ...prev, activeTestMessages: next };
        } );
    }

    // GETTERS AND SETTERS - lots of :)
    toggleShowControlsDrawer(value: boolean): void {
         this.setState({
            showControlsDrawer: value
        });
    }

    toggleShowTestOutputPanel(value: boolean): void {
        this.setState({
            showTestOutputPanel: value
        });
    }

    toggleShowErrorMessagesPanel(value: boolean): void {
        this.setState({
            showErrorMessagesPanel: value
        });
    }

    setWorkflowExecutionSettings(settings: any): void {
        this.setState({
            workflowExecutionSettings: settings
        }, (): void => {
            if (settings !== undefined) {
                this.toggleWorkflowExecutionSettingsModal(true)
            }
        });
    }

    toggleWorkflowExecutionSettingsModal(value: boolean): void {
        this.setState({
            showWorkflowExecutionSettingsModal: value
        }, (): void => {
            if (value === false) {
                this.setWorkflowExecutionSettings(undefined)
            }
        });
    }

    updateExecutionSetting(key: string, value: any): void {
        let settings = this.state.executionSettings;
        settings[key] = value;
        this.setState({
            executionSettings: settings
        }, (): void => {
            this.temporaryDataStore();
            this.updateAdminFormContext();
        });
    }

    updateSetting(key: string, value: any): void {
        let settings = this.state.settings;
        settings[key] = value;
        this.setState({
            settings: settings
        }, (): void => {
            this.temporaryDataStore();
            this.updateAdminFormContext();
        });
    }

    setActiveEditUiUpdateNode(node: Node): void {
        this.setState({
            activeEditUiUpdateNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleActiveEditUiUpdateModal(true)
            }
        })
    }

    toggleActiveEditUiUpdateModal(value: boolean): void {
        this.setState({
            showUiUpdateModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditUiUpdateNode(undefined)
            }
        });
    }

    setActiveEditMoveNode(node: Node): void {
        this.setState({
            activeEdiMoveNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowMoveModal(true);
            }
        });
    }

    toggleShowMoveModal(value: boolean): void {
        this.setState({
            showMoveModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditMoveNode(undefined);
            }
        });
    }

    setActiveEditHumanNode(node: Node): void {
        this.setState({
            activeEditHumanNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowHumanModal(true);
            }
        });
    }

    toggleShowHumanModal(value: boolean): void {
        this.setState({
            showHumanModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditHumanNode(undefined);
            }
        });
    }

    toggleShowTestWindow(value: boolean): void {
        this.setState({
            showTestWindow: value
        });
    }

    clearTests(): void {
        this.resetTestCompletes();
        this.resetTestErrors();
        this.resetProcessing();
    }

    resetTestErrors(): void {
        this.setState({
            testErrored: [],
            nodeTestOutputs: []
        });
    }

    addTestError(id: string): void {
        let erroredTests = this.state.testErrored;
        erroredTests.push(id);
        let processing: string[] = this.state.testProcessing;
        if (processing.includes(id)) {
            delete processing[processing.indexOf(id)];
        }
        this.setState({
            testErrored: erroredTests,
            testProcessing: processing
        });
    }

    resetTestCompletes() {
        this.setState({
            testComplete: []
        });
    }

    resetProcessing(): void {
        this.setState({
            testProcessing: []
        });
    }

    addTestProcessing(id: string): void {
        let processing: string[] = this.state.testProcessing;
        if (!processing.includes(id)) {
            processing.push(id);
            this.setState({
                testProcessing: processing
            });
        }

    }

    successfullNodeTest(id: string): void {
        let successfulTests = this.state.testComplete;
        successfulTests.push(id);
        let processing: string[] = this.state.testProcessing;
        if (processing.includes(id)) {
            delete processing[processing.indexOf(id)];
        }
        this.setState({
            testComplete: successfulTests,
            testProcessing: processing
        });
    }

    setActiveEditeSampleDataNode(node: Node): void {
        this.setState({
            activeSampleDataNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowSampleDataModal(true);
            }
        });
    }

    toggleShowSampleDataModal(value: boolean): void {
        this.setState({
            showSampleDataModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditeSampleDataNode(undefined);
            }
        });
    }

    setActiveEditSortNode(node: Node): void {
        this.setState({
            activeEditSortNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowSortModal(true);
            }
        });
    }

    toggleShowSortModal(value: boolean): void {
        this.setState({
            showSortModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditSortNode(undefined);
            }
        });
    }

    setActiveEditDataResetNode(node: Node): void {
        this.setState({
            activeEditDataResetNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowDataResetModal(true);
            }
        });
    }

    toggleShowDataResetModal(value: boolean): void {
        this.setState({
            showDataResetModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditDataResetNode(undefined);
            }
        });
    }

    setActiveEditInjectionNode(node: Node): void {
        this.setState({
            activeEditInjectionNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowInjectionModal(true);
            }
        });
    }

    toggleShowInjectionModal(value: boolean): void {
        this.setState({
            showInjectionModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditInjectionNode(undefined);
            }
        });
    }

    setActiveEditExtractionNode(node: Node): void {
        this.setState({
            activeExtractionNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowExtractionModal(true);
            }
        });
    }

    toggleShowExtractionModal(value: boolean): void {
        this.setState({
            showExtractionModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditExtractionNode(undefined);
            }
        });
    }



    setActiveEditFilterNode(node: Node): void {
        this.setState({
            activeEditFilterNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowFilterModal(true);
            }
        });
    }

    toggleShowFilterModal(value: boolean): void {
        this.setState({
            showFilterModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditFilterNode(undefined);
            }
        });
    }

    setActiveEditConvertNode(node: Node): void {
        this.setState({
            activeEditConvertNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowConvertModal(true);
            }
        });
    }

    toggleShowConvertModal(value: boolean): void {
        this.setState({
            showConvertModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditConvertNode(undefined);
            }
        });
    }

    setActiveEditSummarizeNode(node: Node): void {
        this.setState({
            activeEditSummarizeNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowSummarizeModal(true);
            }
        });
    }

    toggleShowSummarizeModal(value: boolean): void {
        this.setState({
            showSummarizeModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditSummarizeNode(undefined);
            }
        });
    }

    setActiveEditHQCreateNode(node: Node): void {
        this.setState({
            activeEditHQCreateNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowHQCreateModal(true);
            }
        });
    }

    toggleShowHQCreateModal(value: boolean): void {
        this.setState({
            showHQCreateModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditHQCreateNode(undefined);
            }
        });
    }

    setActiveEditHQUploadNode(node: Node): void {
        this.setState({
            activeEditHQUploadNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleHQUploadModal(true);
            }
        });
    }

    toggleHQUploadModal(value: boolean): void {
        this.setState({
            showHQUploadModal: value
        }, () => {
            if (value === false) {
                this.setActiveEditHQUploadNode(undefined);
            }
        });
    }

    setActiveEditSwitchNode(node: Node): void {
        this.setState({
            activeEditSwitchNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowSwitchModal(true);
            }
        });
    }

    toggleShowSwitchModal(value: boolean): void {
        this.setState({
            showSwitchModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditSwitchNode(undefined);
            }
        });
    }

    setActiveEditLoopNode(node: Node): void {
        this.setState({
           activeEditLoopNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowLoopModal(true);
            }
        });
    }

    toggleShowLoopModal(value: boolean): void {
        this.setState({
            showLoopModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditLoopNode(undefined);
            }
        });
    }

    setActiveEditParallelNode(node: Node) {
        this.setState({
            activeEditParallelNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowParallelModal(true);
            }
        });
    }

    toggleShowParallelModal(value: boolean) {
        this.setState({
            showParallelModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditParallelNode(undefined);
            }
        });
    }

    setActiveEditMergeNode(node: Node): void {
        this.setState({
           activeEditMergeNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowMergeModal(true);
            }
        });
    }

    toggleShowMergeModal(value: boolean): void {
        this.setState({
            showMergeModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditMergeNode(undefined);
            }
        });
    }

    setActiveEditHttpNode(node: Node): void {
        this.setState({
            activeEditHttpNode: node
        }, (): void  => {
            if (node !== undefined) {
                this.toggleShowHttpModal(true);
            }
        });
    }

    toggleShowHttpModal(value: boolean): void {
        this.setState({
            showHttpModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditHttpNode(undefined);
            }
        });
    }

    setActiveEditHQFetchNode(node: Node): void {
        this.setState({
            activeEditHQFetchNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowHQFetchModal(true);
            }
        });
    }

    toggleShowHQFetchModal(value: boolean): void {
        this.setState({
            showHQFetchModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditHQFetchNode(undefined);
            }
        });
    }

    setActiveEditSitemapNode(node: Node): void {
        this.setState({
            activeEditSitemapNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleShowSitemapModal(true);
            }
        });
    }

    toggleShowSitemapModal(value: boolean): void {
        this.setState({
            showSitemapModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditSitemapNode(undefined);
            }
        });
    }

    setActiveEditScraperNode(node: Node): void {
        this.setState({
            activeEditScraperNode: node
        }, (): void => {
            if (node !== undefined) {
                this.toggleScraperNodeModalShow(true);
            }
        });
    }

    toggleScraperNodeModalShow(value: boolean): void {
        this.setState({
            showScraperModal: value
        }, (): void => {
            if (value === false) {
                this.setActiveEditScraperNode(undefined);
            }
        });
    }

    setEditableIfNodeConfiguration(config: any): void {
        this.setState({
            editableIfNodeConfiguration: config
        }, (): void => {
            if (config === undefined) {
                this.hideShowIfModal(false);
            } else {
                this.hideShowIfModal(true);
            }
        } );
    }

    hideShowIfModal(value: boolean): void {
        this.setState({
            showIfModal: value
        });
    }

    updateLayoutNodes(data: any): void {
        let nodes = this.state.nodes;
        // layout.nodes = data;
        let datum = data;
        if (nodes.length === 1) {
            // datum = nodes + data;
            console.log('test');
        }

        this.setState({
            nodes: datum,
        }, (): void => {
            this.temporaryDataStore();
            this.updateAdminFormContext();
        });
    }

    updateLayoutEdges(data: any): void {
        // let ed = this.state.edges;
        // layout.edges = data;
        this.setState({
            edges: data
        }, (): void => {
            this.temporaryDataStore();
            this.updateAdminFormContext();
        });
    }

    setEditingTool(tool: Tool|undefined, workflowId: string|undefined): void {
        // debugger;
        let selectedNode = undefined;
        if (workflowId !== undefined) {
            let nodes: WorkflowNode[] = this.state.nodes;
            for (let n of nodes) {
                if (n.id === workflowId) {
                    selectedNode = n;
                }
            }
        }

        this.setState({
            editingTool: tool,
            selectedNode: selectedNode
        }, (): void => {
            if ( tool !== undefined ) {
                this.toggleToolConfigureModal(true)
            }
        })
    }

    cleanToolParams(tool: any): Tool {
        let image: any = undefined;
        if (tool['image'] !== undefined) {
            image = {
                src: hostUrl + tool['image'],
                title: tool['name']
            }
        }
        return {
            id: tool['id'],
            operatorToolId: tool['operatorToolId'],
            description: tool['description'],
            customParameters: tool['customParameters'],
            name: tool['name'],
            image: image,
            enabled: tool['enabled'],
        }
    }

    toggleToolConfigureModal(value: boolean): void {
        if (value === false) {
            this.setState({
                toolConfigureModalOpen: false,
                editingTool: undefined,
                selectedNode: undefined
            })
        } else {
            this.setState({
                toolConfigureModalOpen: value,
            })
        }


    }
    // END OF GETTERS AND SETTERS

    exportWorkflowDataToFile(workflowName: string = 'untitled_workflow') : string {
        if (workflowName !== 'untitled_workflow') {
            workflowName = workflowName.toLowerCase().replace(' ', '_');
        }
        if (this.state.nodes.length > 0) {
            const datum: any = {
                nodes: this.state.nodes,
                edges: this.state.edges
            };
            const blob = new Blob([ JSON.stringify(datum) ], { type: 'text/json' });
            const a = document.createElement('a')
            a.download = workflowName + '.json';
            a.href = window.URL.createObjectURL(blob)
            const clickEvt = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true,
            })
            a.dispatchEvent(clickEvt)
            a.remove()
        } else {
            return "Please add a node to be exported";
        }
        return 'success';
    }

    importWorkflowDataFromFile(importedData: string) : string {
        try {
            const importedDatum = JSON.parse(importedData);
            if (importedDatum !== null &&
                importedDatum !== undefined &&
                importedDatum['nodes'] !== undefined
            ) {
                this.setState({
                    nodes: importedDatum['nodes'],
                    edges: importedDatum['edges']
                }, (): void => {
                    this.updateAdminFormContext();
                });
                return 'success';
            }
        } catch (error) {
            console.error(error);
        }
        return 'Failed to import workflow data';

    }

    updateToolConfiguration(data: any, id: string, operatorId: string): void {
        // debugger;
        let selectedNode: WorkflowNode = this.state.selectedNode;
        let agent: Agent = selectedNode.data.agent;
        let toolConfiguration = {
            id: id,
            operatorId: operatorId,
            data: data
        };

        if (Array.isArray(agent.toolConfiguration)) {
            let toolConfigurations = agent.toolConfiguration;
            let updatedToolConfigurations = []
            for (const config of toolConfigurations) {
                if (config.id !== id) {
                    updatedToolConfigurations.push(config);
                }
            }
            updatedToolConfigurations.push(toolConfiguration);
            agent.toolConfiguration = updatedToolConfigurations;
        } else {
            agent.toolConfiguration = [toolConfiguration];
        }

        let allNodes = this.state.nodes;
        let updatedNodes = [selectedNode];
        for (const node of allNodes) {
            if (node.id !== selectedNode.id) {
                updatedNodes.push(node);
            }
        }

        this.setState({
            nodes: updatedNodes,
            toolConfigureModalOpen: false,
        }, (): void => {
            this.temporaryDataStore();
            this.updateAdminFormContext();
        });
    }

    preloadDate(): void {
        // debugger;
        const key: string = 'create_node_temporary_store_' + this.props.mode;
        let state: string = undefined;
        let whichOption = 'state';
        let stateData = undefined;
        if (typeof window !== "undefined" && window.localStorage.getItem(key) !== null) {
            state = window.localStorage.getItem(key);
            stateData = JSON.parse(state);
        }
        let record: any = this.props.record;
        let recordId = '';
        if (record !== undefined) {
            recordId = record.id;
        }

        if (recordId !== '' && recordId !== stateData?.id) {
            whichOption = 'record';
        }

        if (this.props.mode === 'test') {
            whichOption = 'state';
        }

        if (whichOption === 'state' && stateData !== undefined) {
            // const edges = stateData.edges;
            // for (let edge of edges) {
            //     if (edge.type !== 'deleteButtonEdge' && edge.animated !== true) {
            //         edge.type = 'deleteButtonEdge';
            //     }
            //     if (edge.animated === true) {
            //         edge.type = 'step';
            //     }
            // }
            const nodes: any = this.state.nodes;
            for (let node of nodes) {
                if (node.type === 'workflowLabelNode') {
                    // debugger;
                    // node.style.zIndex = '0'
                }
            }
            this.setState({
                nodes: stateData.nodes,
                edges: stateData.edges,
                settings: stateData.settings,
                activeTestMessages: [],
            }, (): void => {
                // if the data value not equal to the stored value
                if (this.props.record !== undefined &&
                    this.props.record.configuration !== undefined &&
                    (
                        this.props.record.configuration.nodes !== stateData.nodes ||
                        this.props.record.configuration.edges !== stateData.edges ||
                        this.props.record.configuration.settings !== stateData.settings
                    )) {
                    this.updateAdminFormContext();
                }
            });
        }
        if (whichOption === 'record') {
            const record = this.props.record;
            const config = record.configuration;
            if (config !== undefined) {
                this.setState({
                    nodes: config.nodes ?? [],
                    edges: config.edges ?? [],
                    settings: config.settings ?? []
                });
            }
        }

        if (this.state.tools.length <= 0) {
            let token: string = read_cookie('auth');
            let url: string = apiUrl + 'tools?enabled=1';
            const request = new Request(url, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type' : 'application/json',
                    'token' : token,
                    'hq': 'hq'
                })
            });
            fetch(request)
                .then( (response: Response) => {
                    return response.json();
                })
                .then( (data: any) => {
                    if (Array.isArray(data['hydra:member'])) {
                        let tools: any[] = data['hydra:member'];
                        let totalItems: number = data['hydra:totalItems'];
                        let tmpTools: Tool[] = [];
                        for(let i: number = 0; i < tools.length; i++) {
                            let tool: any[] = tools[i];
                            tmpTools.push(this.cleanToolParams(tool))
                        }
                        if (tmpTools.length > 0) {
                            this.setState({
                                tools: tmpTools
                            })
                        }
                    }
                })
                .catch( (error: Error) => {
                    console.log(error);
                });
        }

        this.restoreSocketConnectionDetails();

    }

    removeTemporaryData() {
        this.temporaryDataStore(true);
    }

    temporaryDataStore(remove: boolean = false) {
        if (typeof window !== "undefined") {

            const key: string = 'create_node_temporary_store_' + this.props.mode;
            if (remove) {
                window.localStorage.removeItem(key);
            } else {
                let state = this.state;
                let storageItem = {
                    nodes: state.nodes,
                    edges: state.edges,
                    settings: state.settings,
                    executionSettings: state.executionSettings,
                    activeChatMessages: [],
                    activeTestMessages: [],
                    id: this.props.record?.id ?? undefined
                };
                let val = JSON.stringify(storageItem);
                window.localStorage.setItem(key, val);

            }
        }
    }

    toggleShowModal(value: boolean, clearSelected: boolean = false): void {
        this.setState({
            showModal: (value !== undefined ? value : !this.state.showModal),
            selectedNode: ( clearSelected ? undefined : this.state.selectedNode )
        });
    }

    updateNode(node: WorkflowNode): void {
        let nodes: WorkflowNode[] = this.state.nodes;
        let updatedNodes: WorkflowNode[] = [];
        for (let n of nodes) {
            if (n.id === node.id) {
                updatedNodes.push(node);
            } else {
                updatedNodes.push(n);
            }
        }
        this.setState({
            nodes: updatedNodes
        }, () => {
            this.temporaryDataStore();
            this.updateAdminFormContext();
        });
    }

    setNodeForEdit(node: WorkflowNode): void {
        this.setState({
            selectedNode: node
        }, (): void => this.toggleShowModal(true) );
    }

    deleteNode(node: WorkflowNode): void {
        let nodes: WorkflowNode[] = this.state.nodes;
        let updatedNodes: WorkflowNode[] = [];
        for (let n of nodes) {
            if (n.id !== node.id) {
                updatedNodes.push(n);
            }
        }
        this.setState({
            nodes: updatedNodes
        }, () => this.temporaryDataStore());
    }

    updateAdminFormContext(): void {
        let configuration = {
            nodes: this.state.nodes,
            settings: this.state.settings,
            executionSettings: this.state.executionSettings,
            edges: this.state.edges,
            id: this.props?.record?.id ?? undefined
        };

        if (this.props.setFormValue !== undefined) {
            this.props.setFormValue('configuration', configuration, { shouldDirty: true } );
        }

    }

    setActiveOperator(operator: Operator): void {
        this.setState({
            operator: operator
        }, (): void => {
            if (operator !== undefined) {
                this.createSocketToOperator(operator);
            }
        })
    }

    storeSocketConnectionDetails(): void {
        if (window !== undefined) {
            let payload = {
                'connectorId': this.state.connectorId,
                'operator': this.state.operator,
            };
            window.localStorage.setItem('operator_socket_connection', JSON.stringify(payload));
        }
    }

    clearSocketConnectionDetails(): void {
        if (window !== undefined) {
            window.localStorage.removeItem('operator_socket_connection');
        }
    }

    restoreSocketConnectionDetails(): void {
        if (window !== undefined) {
            let storedValues: string = window.localStorage.getItem('operator_socket_connection');
            storedValues = JSON.parse(storedValues);
            if (storedValues !== null && storedValues !== undefined && storedValues['connectorId'] !== undefined) {
                this.setState({
                    connectorId: storedValues['connectorId'],
                    operator: storedValues['operator']
                });
            }
        }
    }

    /**
     * HQAPI-76. The URL was built from `operator.host` and `operator.token`, and that token is
     * the Operator's own standing secret: it opens every route on that Operator, never expires,
     * and went out in the query string where nginx and browser history both keep it. HQ-API
     * answers a host and a signed ticket now, and the ticket rides in the subprotocol.
     *
     * Asked for on every connection, because the Operator spends a ticket on open.
     */
    async createSocketToOperator(operator: Operator): Promise<void> {
        let connectorId: string = this.state.connectorId ?? this.createRandomString(10);
        let connection: OperatorConnection;
        try {
            connection = await fetchOperatorConnection(operator.id);
        } catch (error) {
            console.warn('Operator connection lookup failed', error);

            return;
        }
        let socketUrl: string = operatorSocketUrl(
            connection,
            '/admin/workflow/test/ws',
            { connector: connectorId }
        );

        try {
            const that: this = this;
            const socket = new WebSocket(socketUrl, operatorSocketProtocols(connection));
            socket.onopen = async function (event: Event): Promise<void> {
                // that.socket = socket;
                // that.operator = operator;
                // that.connectorId = connectorId;
                // await that.sendMessageToOperator('/greeting', false)
                const op: Operator = (that.state.operator ?? operator);
                that.setState({
                    operator: op,
                    testSocket: socket,
                    connectorId: connectorId
                }, (): void => {
                    if (that.state.activeTestMessages.length === 0) {
                        that.sendMessageToOperator('/greeting', false)
                    }
                    that.adjustOperatorAvailability(op.id.toString(), 'up');
                    // that.storeSocketConnectionDetails()

                })
            }
            // socket.addEventListener('open', async () => {
            //
            // });
            socket.onmessage = function (event: MessageEvent<any>): void {
                // debugger;
                let message: string = event.data;
                message = JSON.parse(message);
                // console.log(message);
                // A `token` frame is one of two things: a **card**, carrying `component` and
                // `payload`, or one **delta** of a streaming reply. The Operator sends both under
                // `type: 'token'` because that is the customer composer's long-standing contract,
                // which is deliberately left alone.
                if (message['type'] === 'token' && message['component'] !== undefined) {
                    // Held as `data` rather than spread, because ChatWindow renders
                    // `message.data.subject` and `message.data.body`. A card whose name ChatWindow
                    // has no branch for is simply not drawn, which is the intended behaviour.
                    const msg: any = {
                        type: message['component'],
                        data: message['payload'],
                    };
                    that.setState( (prev: Readonly<WorkflowStateType>) => ({
                        activeTestMessages: [...prev.activeTestMessages, msg],
                    } ) );
                } else if (message['type'] === 'token') {
                    // This read `message['response']['id']`, and a delta frame has **no `response`
                    // key at all** - it is `{ type, delta, id }` at the top level. So every
                    // streamed token threw a TypeError here and the tester has never shown a
                    // streaming reply, while the portal, reading the same frames correctly,
                    // always has. That difference is why the tester was a poor preview of the
                    // product.
                    that.streamingMsgId = message['id'];
                    that.bufferEmailDelta(message['delta'] ?? '');
                }
                if (message['type'] === 'node_output') {
                    let datum: string = '';
                    try {
                        datum = JSON.parse(message['data']);
                        datum = JSON.stringify(datum, undefined, 4)
                    } catch (error) {
                        datum = message['data'];
                    }

                    let msg: { title: string; data: string } = {
                        'title': message['message'] + ' - ' + message['node_id'],
                        'data': datum
                    }
                    that.setState( (prev: Readonly<WorkflowStateType>) =>  ({
                        nodeTestOutputs: [ ...prev.nodeTestOutputs, msg ]
                    }) );
                }
                if (message['completed'] !== undefined) {
                    that.setState({
                        // testComplete: [],
                        testOperatorThinking: false
                    });
                }
                if (message['interrupt'] !== undefined) {
                    const msg = {
                        'message': message['interrupt'],
                        'author': 'Operator',
                        'type': 'operator',
                        'conditional': message['conditional'],
                    }
                    that.setState((prev: Readonly<WorkflowStateType>) => ({
                        testOperatorThinking: false,
                        interruptKey: message['h_i_t_l'],
                        activeTestMessages: [...prev.activeTestMessages, msg],
                        interruptWaiting: true,
                        conditionalInterrupt: message['conditional']
                    }) );
                }
                if (message['action'] !== undefined) {
                    const id: string = message['data']['node_id'];
                    if (message['data']['status'] === 'complete') {
                        that.successfullNodeTest(id)
                    }
                    if (message['data']['status'] === 'test-reset') {
                        that.resetTestCompletes()
                    }
                    if (message['data']['status'] === 'processing') {
                        that.addTestProcessing(id);
                    }
                    if (message['data']['status'] === 'error') {
                        that.addTestError(id);
                    }
                }
                if (message['error'] !== undefined) {
                    const msg = {
                        'message': message['error'],
                        'author': operator.name,
                        'type': 'operator'
                    }
                    msg.author = 'Operator';
                    that.setState({
                        errorMessages: [...that.state.errorMessages, msg],
                    });
                }
                if (message['response'] !== undefined && message['type'] === 'greeting') {

                    const msg: { 'message': string, 'author': string, 'type': string } = {
                        'message': message['response'],
                        'author': 'Operator',
                        'type': 'operator'
                    }
                    that.setState( (prev) => ({
                        activeTestMessages: [...prev.activeTestMessages, msg],
                        testOperatorThinking: false
                    }) );
                }
                if (message['type'] === 'tool_output') {
                    if (message['data']['type'] === 'status_update' && message['data']['data']?.['icon'] === 'error') {
                        // The workflow talking to the person rather than reporting progress, such as
                        // a refusal. As a status it only showed while the Operator was thinking, and
                        // the run completes straight after, so it vanished. NN's composer does the same.
                        const msg = {
                            'message': message['data']['data']['value'],
                            'author': 'Operator',
                            'type': 'operator'
                        }
                        that.setState( (prev: Readonly<WorkflowStateType>) => ({
                            activeTestMessages: [...prev.activeTestMessages, msg],
                        }) );
                    } else if (message['data']['type'] === 'status_update') {
                        that.setState({
                            activeStatus: message['data']['data'],
                        });
                    } else {
                        const msg = {
                            'type': 'operator_tool_output',
                            'data': message['data']
                        }

                        that.setState( (prev: Readonly<WorkflowStateType>) => ({
                            activeTestMessages: [...prev.activeTestMessages, msg],
                        }) );
                    }
                }
            }
            socket.onclose = (event: CloseEvent): void => {
                if (that.state.operator !== undefined) {
                    that.adjustOperatorAvailability(that.state.operator.id.toString(), 'down');
                }

                that.setState({
                    operator: undefined,
                    activeTestMessages: [],
                }, (): void => {
                    that.clearSocketConnectionDetails();
                });
                console.warn("My WebSocket closed:", event);
            };

            socket.onerror = (event: Event): void => {
                if (that.state.operator !== undefined) {
                    that.adjustOperatorAvailability(that.state.operator.id.toString(), 'down');
                }

                that.setState({
                    operator: undefined,
                    activeTestMessages: [],
                }, (): void => {
                    that.clearSocketConnectionDetails();
                });
                console.error("My WebSocket error:", event);
            };

        } catch (error) {
            console.log(error);
        }

    }

    adjustOperatorAvailability(operatorId: string, direction: string = 'up'): void {
        let token: string = read_cookie('auth');
        let url: string = hostUrl + 'hq/availability/adjustment';
        url = UseUrlManagerCleanUrl({ url: url });
        const payload = {
            'operatorId': operatorId,
            'direction': direction
        };
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        fetch(request)
            .then( (response: Response): Promise<any> => {
                return response.json();
            })
            .then( (data: any): void => {
                console.log(data);
            })
            .catch( (error: Error): void => {
                console.log(error);
            })
    }

    sendWorkflowToOperator(): { nodes: WorkflowNode[], edges: Edge[], settings: any[]  } {
        return {
            nodes: this.state.nodes,
            edges: this.state.edges.filter( (edge: any): boolean => edge.type !== 'workflowLabelNode' ),
            settings: this.state.settings
        }
    }

    createRandomString(length: number): string {
        const chars: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result: string = "";
        for (let i: number = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }

    findInvalidBtoaChars(str: string): any[] {
        const invalidChars: any[] = [];

        for (let i: number = 0; i < str.length; i++) {
            const char: string = str[i];
            const code: any = char.codePointAt(0);
            if (code > 255) {
                invalidChars.push({ index: i, char, code: `U+${code.toString(16).toUpperCase()}` });
            }
        }

        return invalidChars;
    }

    async sendMessageToOperator(message: string, encryptValue: boolean = true): Promise<void> {
        if (this.state.testSocket !== undefined  && this.state.testSocket.readyState === WebSocket.OPEN) {
            // debugger;
            if (!encryptValue) {
                try {
                    this.state.testSocket.send(message);
                    this.setState({
                        testOperatorThinking: true
                    });
                } catch (error) {
                    console.log(error);
                }

            } else {
                let payload: any = {
                    'payload': this.sendWorkflowToOperator(),
                    'prompt': message,
                    'type': 'test',
                    'sessionId': this.createRandomString(10),
                    'connectorId': this.state.connectorId,
                    'operatorName': this.state.operator.name,
                    'adminUsername': this.state.adminUsername,
                    'h_i_t_l': this.state.interruptKey,
                    workflowId: this.state.currentRecord.id ?? undefined
                }

                payload = JSON.stringify(payload);
                const conflictingCharacters: any[] = this.findInvalidBtoaChars(payload);
                if (conflictingCharacters.length > 0) {
                    let errorMessage: string = "Unable to convert some characters into required format. Please check for the following and try again:\n\n";
                    for(let i: number = 0; i < conflictingCharacters.length; i++) {
                        const conflicter: any = conflictingCharacters[i];
                        const num: string = (i + 1).toString()
                        errorMessage += num + ") Character: " + conflicter.char + ' has code: ' + conflicter.code;
                    }
                    let msg: { message: string, author: string, type: string } = {
                        'message': errorMessage,
                        'author': 'System',
                        'type': 'operator'
                    };
                    this.setState({
                        errorMessages: [...this.state.errorMessages, msg],
                        showErrorMessagesPanel: true
                    });
                } else {
                    try {
                        this.state.testSocket.send(payload);
                        this.setState({
                            testOperatorThinking: true,
                            // testComplete: tempTestComplete,
                            interruptKey: '',
                            interruptWaiting: false,
                            conditionalInterrupt: false
                        }, (): void => {
                            if (message !== "continue" && message !== "false") {
                                const msg = {
                                    'message': message,
                                    'author': this.state.adminUsername,
                                    'type': 'user'
                                }
                                if (message.length > 0) {
                                    this.setState( (prev: any) => ({
                                        activeTestMessages: [...prev.activeTestMessages, msg]
                                    }));
                                }
                            }
                        });
                    } catch (error) {
                        console.log(error);
                    }
                }
            }
        }
    }

    async disconnectOperator(): Promise<void> {
        if (this.state.testSocket !== undefined) {
            const payload = {
                'message': '/stop',
                'connectorId': this.state.connectorId,
            };
            this.state.testSocket.send('/stop');
            this.state.testSocket.close();
            if (this.state.operator !== undefined) {
                this.adjustOperatorAvailability(this.state.operator.id, 'down');
            }

            this.setState({
                operator: undefined,
                testSocket: undefined,
                activeTestMessages: [],
                connectorId: undefined
            });
        }

    }


    render(): JSX.Element {
        return (
            <WorkflowContext.Provider value={{
                ...this.state,
                toggleShowModal: this.toggleShowModal.bind(this),
                setNodeForEdit: this.setNodeForEdit.bind(this),
                updateNode: this.updateNode.bind(this),
                deleteNode: this.deleteNode.bind(this),
                toggleToolConfigureModal: this.toggleToolConfigureModal.bind(this),
                setEditingTool: this.setEditingTool.bind(this),
                updateToolConfiguration: this.updateToolConfiguration.bind(this),
                updateLayoutNodes: this.updateLayoutNodes.bind(this),
                updateLayoutEdges: this.updateLayoutEdges.bind(this),
                hideShowIfModal: this.hideShowIfModal.bind(this),
                setEditableIfNodeConfiguration: this.setEditableIfNodeConfiguration.bind(this),
                createSocketToOperator: this.createSocketToOperator.bind(this),
                setActiveOperator: this.setActiveOperator.bind(this),
                disconnectOperator: this.disconnectOperator.bind(this),
                sendMessageToOperator: this.sendMessageToOperator.bind(this),
                toggleScraperNodeModalShow: this.toggleScraperNodeModalShow.bind(this),
                setActiveEditScraperNode: this.setActiveEditScraperNode.bind(this),
                toggleShowSitemapModal: this.toggleShowSitemapModal.bind(this),
                setActiveEditSitemapNode: this.setActiveEditSitemapNode.bind(this),
                setActiveEditHQFetchNode: this.setActiveEditHQFetchNode.bind(this),
                toggleShowHQFetchModal: this.toggleShowHQFetchModal.bind(this),
                setActiveEditHttpNode: this.setActiveEditHttpNode.bind(this),
                toggleShowHttpModal: this.toggleShowHttpModal.bind(this),
                setActiveEditMergeNode: this.setActiveEditMergeNode.bind(this),
                toggleShowMergeModal: this.toggleShowMergeModal.bind(this),
                setActiveEditParallelNode: this.setActiveEditParallelNode.bind(this),
                toggleShowParallelModal: this.toggleShowParallelModal.bind(this),
                setActiveEditLoopNode: this.setActiveEditLoopNode.bind(this),
                toggleShowLoopModal: this.toggleShowLoopModal.bind(this),
                setActiveEditSwitchNode: this.setActiveEditSwitchNode.bind(this),
                toggleShowSwitchModal: this.toggleShowSwitchModal.bind(this),
                setActiveEditHQUploadNode: this.setActiveEditHQUploadNode.bind(this),
                toggleHQUploadModal: this.toggleHQUploadModal.bind(this),
                setActiveEditHQCreateNode: this.setActiveEditHQCreateNode.bind(this),
                toggleShowHQCreateModal: this.toggleShowHQCreateModal.bind(this),
                setActiveEditSummarizeNode: this.setActiveEditSummarizeNode.bind(this),
                toggleShowSummarizeModal: this.toggleShowSummarizeModal.bind(this),
                setActiveEditConvertNode: this.setActiveEditConvertNode.bind(this),
                toggleShowConvertModal: this.toggleShowConvertModal.bind(this),
                setActiveEditFilterNode: this.setActiveEditFilterNode.bind(this),
                toggleShowFilterModal: this.toggleShowFilterModal.bind(this),
                setActiveEditMoveNode: this.setActiveEditMoveNode.bind(this),
                toggleShowMoveModal: this.toggleShowMoveModal.bind(this),
                setActiveEditExtractionNode: this.setActiveEditExtractionNode.bind(this),
                toggleShowExtractionModal: this.toggleShowExtractionModal.bind(this),
                setActiveEditInjectionNode: this.setActiveEditInjectionNode.bind(this),
                toggleShowInjectionModal: this.toggleShowInjectionModal.bind(this),
                setActiveEditDataResetNode: this.setActiveEditDataResetNode.bind(this),
                toggleShowDataResetModal: this.toggleShowDataResetModal.bind(this),
                setActiveEditSortNode: this.setActiveEditSortNode.bind(this),
                toggleShowSortModal: this.toggleShowSortModal.bind(this),
                setActiveEditeSampleDataNode: this.setActiveEditeSampleDataNode.bind(this),
                toggleShowSampleDataModal: this.toggleShowSampleDataModal.bind(this),
                resetTestCompletes: this.resetTestCompletes.bind(this),
                clearTests: this.clearTests.bind(this),
                toggleShowTestWindow: this.toggleShowTestWindow.bind(this),
                setActiveEditHumanNode: this.setActiveEditHumanNode.bind(this),
                toggleShowHumanModal: this.toggleShowHumanModal.bind(this),
                updateSetting: this.updateSetting.bind(this),
                updateExecutionSetting: this.updateExecutionSetting.bind(this),
                setWorkflowExecutionSettings: this.setWorkflowExecutionSettings.bind(this),
                toggleWorkflowExecutionSettingsModal: this.toggleWorkflowExecutionSettingsModal.bind(this),
                toggleShowErrorMessagesPanel: this.toggleShowErrorMessagesPanel.bind(this),
                adjustOperatorAvailability: this.adjustOperatorAvailability.bind(this),
                toggleShowTestOutputPanel: this.toggleShowTestOutputPanel.bind(this),
                exportWorkflowDataToFile: this.exportWorkflowDataToFile.bind(this),
                importWorkflowDataFromFile: this.importWorkflowDataFromFile.bind(this),
                toggleShowControlsDrawer: this.toggleShowControlsDrawer.bind(this),
                setActiveEditUiUpdateNode: this.setActiveEditUiUpdateNode.bind(this),
                toggleActiveEditUiUpdateModal: this.toggleActiveEditUiUpdateModal.bind(this),
            }} >
                { this.props.children }
            </WorkflowContext.Provider>
        );
    }

}

export default WorkflowContextProvider;